import { useMemo } from "react";
import styles from "./ProjectDetails.module.css";

import { useNavigate, useParams } from "react-router-dom";
import { useFetch } from "shared/utils/useFetch";
import { getIcon } from "shared/utils/iconMap";

import Collaborate from "features/collaborate";
import PersonalDetails from "features/personal-details";
import ProjectHighlights from "shared/components/ProjectHighlights";
import { formatRouteLabel } from "shared/utils/text";

const ProjectDetails = () => {
  const navigate = useNavigate();
  const { projectName } = useParams();
  const { data: projects } = useFetch("/api/featured-projects", []);

  const project = useMemo(
    () => projects.find((item) => item.slug === projectName),
    [projects, projectName]
  );
  const projectTitle = formatRouteLabel(projectName);
  const goToProjects = () => navigate("/projects");
  const openProjectUrl = () => {
    window.open(project.external, "_blank", "noopener,noreferrer");
  };

  // Map iconName strings from the API to actual FontAwesome icon objects
  const highlightsWithIcons = useMemo(
    () =>
      (project?.highlights || []).map((h) => ({
        ...h,
        icon: getIcon(h.iconName),
      })),
    [project]
  );

  if (!project) {
    return (
      <div className={styles.content}>
        <div className={styles.row}>
          <div className={styles.personalDetails}>
            <PersonalDetails />
          </div>
          <div className={styles.detailsContainer}>
            <button type="button" onClick={goToProjects} className={styles.backButton}>
              &#8592; All Projects
            </button>
            <div className={styles.outletWrapper}>
              <h1>Project Not Found</h1>
              <p>The project you're looking for doesn't exist.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.content}>
      <div className={styles.row}>
        <div className={styles.personalDetails}>
          <PersonalDetails />
        </div>

        <div className={styles.detailsContainer}>
          <button type="button" onClick={goToProjects} className={styles.backButton}>
            &#8592; All Projects
          </button>

          <div className={styles.outletWrapper}>
            <div className={styles.coverImageContainer}>
              <img
                className={styles.coverImage}
                src={project.cover}
                alt={`${project.title} cover`}
              />
            </div>
            <div className={styles.projectDescription}>
              <h1 className={styles.title}>{projectTitle}</h1>
              <h3 className={styles.subtitle}>{project.subtitle}</h3>
              <p className={styles.description}>{project.description}</p>
            </div>
            <div className={styles.buttonRow}>
              <button type="button" className={styles.button} onClick={openProjectUrl}>
                <span className={styles.buttonText}>View Live</span>
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={goToProjects}
              >
                <span className={styles.statLabel}>More Projects &#8594;</span>
              </button>
            </div>
            <ProjectHighlights highlights={highlightsWithIcons} />
            {project.images.map((image, index) => (
              <div key={`${project.slug}-${index}`} className={styles.previewImagesContainer}>
                <img
                  className={styles.previewImage}
                  src={image}
                  alt={`${project.title} preview ${index + 1}`}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.collabContainer}>
          <Collaborate />
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
